import type { Hotkey } from "@replo/design-system/utils/hotkeys";

import React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { Checkbox } from "@v1/components/designSystem/checkbox/Checkbox";
import { HotkeyIndicator } from "@v1/components/designSystem/hotkeys/HotKeyIndicator";
import Switch from "@v1/components/designSystem/switch/Switch";
import { Check, ChevronRight } from "lucide-react";

type BaseMenuItemProps = React.PropsWithChildren<{
  size?: "sm" | "base";
  disabled?: boolean;
  layoutClassName?: string;
  UNSAFE_className?: string;
  onHover?: () => void;
  toolTipText?: string | null | undefined;
}>;

export type DefaultVariant = {
  variant: "default";
  selected?: boolean;
  startEnhancer?: React.ReactNode;
};

export type DangerVariant = {
  variant: "danger";
  selected?: boolean;
  startEnhancer?: React.ReactNode;
};

export type CommandVariant = {
  variant: "command";
  shortcut?: string;
  startEnhancer?: React.ReactNode;
};

export type ExpandableVariant = {
  variant: "expandable";
  startEnhancer?: React.ReactNode;
};

export type CheckboxVariant = {
  variant: "checkbox";
  selected?: boolean;
};

export type SwitchVariant = {
  variant: "switch";
  isOn: boolean;
};

export type CustomVariant = {
  variant: "custom";
  selected?: boolean;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
};

export type ProductVariant = {
  variant: "product";
  selected?: boolean;
  startEnhancer?: React.ReactNode;
  hasCheckbox?: boolean;
};

export type MenuItemProps = BaseMenuItemProps &
  (
    | DefaultVariant
    | DangerVariant
    | CommandVariant
    | ExpandableVariant
    | CheckboxVariant
    | SwitchVariant
    | CustomVariant
    | ProductVariant
  ) &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
  (props, ref) => {
    const size = props.size ?? "base";
    const content = (
      <button
        ref={ref}
        role="menuitem"
        className={twMerge(
          "hover:bg-selectable-hover group-data-[selected=true]:bg-selectable-hover relative flex w-full shrink cursor-pointer items-center justify-between gap-1.5 rounded-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
          size === "sm" && "h-6 min-h-6 px-2 py-1 text-xs",
          size === "base" && "h-8 min-h-8 px-2 py-1.5 text-sm",
          "selected" in props &&
            props.selected &&
            "bg-menu-item-selected hover:bg-menu-item-selected group-data-[selected=true]:bg-menu-item-selected",
          props.variant === "danger" && "text-rose",
          props.layoutClassName,
          props.UNSAFE_className,
        )}
        disabled={props.disabled}
        onMouseEnter={props.onHover}
        {...props}
      >
        <div className="mr-auto flex min-w-0 flex-1 items-center justify-items-stretch gap-2">
          {"startEnhancer" in props && props.startEnhancer}
          {props.variant === "checkbox" && (
            <Checkbox checked={props.selected} />
          )}
          <div className="flex min-w-0 flex-1">
            <div className="truncate">{props.children}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {"shortcut" in props && props.shortcut && (
            <HotkeyIndicator hotkey={props.shortcut as Hotkey} title="" />
          )}
          {"endEnhancer" in props && props.endEnhancer}
          {(props.variant === "default" || props.variant === "danger") &&
            props.selected && <Check className="icon-size-default" />}
          {props.variant === "expandable" && (
            <ChevronRight className="icon-size-default" />
          )}
          {props.variant === "switch" && (
            <Switch size={props.size ?? "base"} isOn={props.isOn} />
          )}
          {props.variant === "product" && props.hasCheckbox && (
            <Checkbox checked={props.selected} />
          )}
        </div>
      </button>
    );
    if (props.toolTipText) {
      return (
        <Tooltip content={props.toolTipText} triggerAsChild>
          {content}
        </Tooltip>
      );
    } else {
      return content;
    }
  },
);

MenuItem.displayName = "MenuItem";

export { MenuItem };
